import { useEffect, useRef, useState } from 'react';
import { Image, ImageSourcePropType, Linking, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import BookCard from '../components/BookCard';
import BottomTabBar from '../components/BottomTabBar';
import SectionHeader from '../components/SectionHeader';
import { BrandHeader } from '../components/AppHeader';
import GradientBackground from '../components/GradientBackground';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { Audiobook, AudiobookCategory, AudiobookPerson, AudiobookTopic, getMobileHome, MobileHomeData } from '../api/audiobooks';
import { getSponsors } from '../api/sponsors';
import { getRecentlyViewed, RecentlyViewedItem } from '../utils/recentlyViewed';
import { getSelectedProfileId } from '../api/profiles';
import { narratorName } from '../utils/audiobookPeople';

type Props = NativeStackScreenProps<RootStackParamList, 'Home'>;
type BookListItem = { key: string; id?: number; slug?: string; title: string; author: string; image: ImageSourcePropType };
type CategoryListItem = { key: string; id?: number; type?: 'topic' | 'category'; label: string; image: ImageSourcePropType };
type NarratorListItem = { key: string; id?: number; name: string; image: ImageSourcePropType };
type SponsorListItem = { key: string; name: string; url?: string | null; logo: ImageSourcePropType };
type HomeSectionListItem = { key: string; title: string; params?: { topic_id?: number; category_id?: number }; books: BookListItem[] };

const continueThumb = require('../../assets/aural-continue-thumb.png');
const logoSun = require('../../assets/aural-logo-sun.png');
const logoVtv = require('../../assets/aural-logo-vtv.png');

const sponsorCardWidth = 180;
const sponsorCardGap = 14;
const homeCacheTtl = 60 * 1000;

let cachedHomeData: MobileHomeData | null = null;
let cachedHomeAt = 0;
let cachedHomePromise: Promise<MobileHomeData> | null = null;

const fairyTales = [
  { image: require('../../assets/aural-book-1.png') },
  { image: require('../../assets/aural-book-2.png') },
  { image: require('../../assets/aural-book-3.png') },
];

const poems = [
  { image: require('../../assets/aural-book-4.png') },
  { image: require('../../assets/aural-book-5.png') },
  { image: require('../../assets/aural-book-1.png') },
];

const childrenStories = [
  { image: require('../../assets/aural-bestseller-1.png') },
  { image: require('../../assets/aural-bestseller-2.png') },
  { image: require('../../assets/aural-bestseller-3.png') },
];

const readTogether = [
  { image: require('../../assets/aural-podcast-1.png') },
  { image: require('../../assets/aural-podcast-2.png') },
  { image: require('../../assets/aural-podcast-3.png') },
];

const fallbackCategories = [
  { image: require('../../assets/aural-category-1.png') },
  { image: require('../../assets/aural-category-2.png') },
  { image: require('../../assets/aural-category-3.png') },
];

const fallbackNarrators = [
  { image: require('../../assets/aural-author-1.png') },
  { image: require('../../assets/aural-author-2.png') },
  { image: require('../../assets/aural-author-3.png') },
];

const fallbackSponsors: SponsorListItem[] = [
  { key: 'fallback-vtv', name: 'VTV', url: 'https://vtv.vn', logo: logoVtv },
  { key: 'fallback-sun', name: 'Mặt Trời', url: 'https://vtv.vn', logo: logoSun },
];

function imageSource(url: string | null | undefined, fallback: ImageSourcePropType): ImageSourcePropType {
  return url ? { uri: url } : fallback;
}

function audiobookAuthor(item: Audiobook) {
  return narratorName(item);
}

function mapAudiobook(item: Audiobook, fallback: ImageSourcePropType): BookListItem {
  return {
    key: String(item.id),
    id: item.id,
    slug: item.slug,
    title: item.name,
    author: audiobookAuthor(item),
    image: imageSource(item.cover, fallback),
  };
}

function mapTopic(item: AudiobookTopic, index: number): CategoryListItem {
  return {
    key: `topic-${item.id}`,
    id: item.id,
    type: 'topic',
    label: item.name,
    image: imageSource(item.image, fallbackCategories[index % fallbackCategories.length].image),
  };
}

function mapCategory(item: AudiobookCategory, index: number): CategoryListItem {
  return {
    key: `category-${item.id}`,
    id: item.id,
    type: 'category',
    label: item.name,
    image: imageSource(item.image, fallbackCategories[index % fallbackCategories.length].image),
  };
}

function mapNarrator(item: AudiobookPerson, index: number): NarratorListItem {
  return {
    key: String(item.id),
    id: item.id,
    name: item.name,
    image: imageSource(item.avatar, fallbackNarrators[index % fallbackNarrators.length].image),
  };
}

function comparePeopleOrder(left: AudiobookPerson, right: AudiobookPerson) {
  return (left.order ?? 0) - (right.order ?? 0) || left.name.localeCompare(right.name, 'vi');
}

function fallbackBookImage(index: number) {
  const fallbackImages = [
    fairyTales[0].image,
    poems[0].image,
    childrenStories[0].image,
    readTogether[0].image,
  ];

  return fallbackImages[index % fallbackImages.length];
}

function SkeletonBlock({ style }: { style?: object }) {
  return <View style={[styles.skeletonBlock, style]} />;
}

function HomeSkeleton() {
  return (
    <>
      <View style={styles.section}>
        <SkeletonBlock style={styles.skeletonTitle} />
        <View style={styles.skeletonContinue}>
          <SkeletonBlock style={styles.skeletonContinueImage} />
          <View style={styles.skeletonContinueText}>
            <SkeletonBlock style={styles.skeletonLineLarge} />
            <SkeletonBlock style={styles.skeletonLineSmall} />
            <SkeletonBlock style={styles.skeletonProgress} />
          </View>
        </View>
      </View>
      {[0, 1, 2, 3].map((section) => (
        <View key={section} style={styles.section}>
          <SkeletonBlock style={styles.skeletonTitle} />
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {[0, 1, 2].map((item) => (
              <View key={item} style={styles.skeletonCard}>
                <SkeletonBlock style={styles.skeletonCover} />
                <SkeletonBlock style={styles.skeletonBookTitle} />
                <SkeletonBlock style={styles.skeletonBookAuthor} />
              </View>
            ))}
          </ScrollView>
        </View>
      ))}
    </>
  );
}

export default function HomeScreen({ navigation }: Props) {
  const [homeData, setHomeData] = useState<MobileHomeData | null>(null);
  const [homeLoaded, setHomeLoaded] = useState(false);
  const [recent, setRecent] = useState<RecentlyViewedItem | null>(null);
  const [sponsors, setSponsors] = useState<SponsorListItem[]>(fallbackSponsors);
  const scrollRef = useRef<ScrollView>(null);
  const sponsorScrollRef = useRef<ScrollView>(null);

  const openDownloads = (title = 'Danh sách', params?: { topic_id?: number; category_id?: number; person_id?: number }) => {
    navigation.navigate('Downloads', { title, ...params });
  };

  const refreshHome = (mounted = true, force = false) => {
    const now = Date.now();

    if (!force && cachedHomeData && now - cachedHomeAt < homeCacheTtl) {
      setHomeData(cachedHomeData);
      setHomeLoaded(true);
      return;
    }

    setHomeLoaded(false);
    const request = force || !cachedHomePromise ? getMobileHome(10, getSelectedProfileId()) : cachedHomePromise;
    cachedHomePromise = request;

    request
      .then((data) => {
        cachedHomeData = data;
        cachedHomeAt = Date.now();
        if (mounted) {
          setHomeData(data);
          setHomeLoaded(true);
        }
      })
      .catch(() => {
        if (mounted) {
          setHomeData(null);
          setHomeLoaded(true);
        }
      })
      .finally(() => {
        if (cachedHomePromise === request) {
          cachedHomePromise = null;
        }
      });
  };

  useEffect(() => {
    let mounted = true;
    refreshHome(mounted);

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const loadRecent = () => setRecent(getRecentlyViewed());
    loadRecent();
    const unsubscribe = navigation.addListener('focus', loadRecent);
    return unsubscribe;
  }, [navigation]);

  useEffect(() => {
    let mounted = true;

    getSponsors()
      .then((items) => {
        if (!mounted || !items.length) {
          return;
        }

        setSponsors(items.map((item, index) => ({
          key: String(item.id),
          name: item.name,
          url: item.url,
          logo: imageSource(item.logo, fallbackSponsors[index % fallbackSponsors.length].logo),
        })));
      })
      .catch(() => {
        if (mounted) {
          setSponsors(fallbackSponsors);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (sponsors.length <= 1) {
      return;
    }

    let currentIndex = 0;
    const timer = setInterval(() => {
      currentIndex = (currentIndex + 1) % sponsors.length;
      sponsorScrollRef.current?.scrollTo({
        x: currentIndex * (sponsorCardWidth + sponsorCardGap),
        animated: true,
      });
    }, 3200);

    return () => clearInterval(timer);
  }, [sponsors.length]);

  const openBook = (item?: BookListItem | Audiobook) => {
    navigation.navigate('BookOverview', {
      id: item && 'id' in item ? item.id : undefined,
      slug: item && 'slug' in item ? item.slug : undefined,
    });
  };

  const continueBook = homeData?.continue_listening?.[0] ?? homeData?.featured?.[0];
  const continueTitle = recent?.title ?? continueBook?.name ?? '';
  const continueAuthor = recent?.author ?? (continueBook ? audiobookAuthor(continueBook) : '');
  const continueChapter = recent?.chapterTitle ?? '';
  const continueImageSource = recent?.cover ? { uri: recent.cover } : imageSource(continueBook?.cover, continueThumb);
  const continueProgress = recent?.duration ? Math.min(((recent.position ?? 0) / recent.duration) * 100, 100) : 0;

  const narratorItems = homeData?.narrators?.length ? [...homeData.narrators].sort(comparePeopleOrder).map(mapNarrator) : [];
  const categoryItems = homeData?.topics?.length
    ? homeData.topics.map(mapTopic)
    : homeData?.categories?.length
      ? homeData.categories.map(mapCategory)
      : [];
  const homeSections: HomeSectionListItem[] = homeData?.sections?.length
    ? homeData.sections.map((section, index) => ({
      key: String(section.id ?? `${section.source_type ?? 'section'}-${section.source_id ?? index}`),
      title: section.name ?? section.topic?.name ?? section.category?.name ?? 'Danh sách',
      params: section.source_type === 'topic' && section.source_id
        ? { topic_id: section.source_id }
        : section.source_type === 'category' && section.source_id
          ? { category_id: section.source_id }
          : undefined,
      books: section.audiobooks.map((item) => mapAudiobook(item, fallbackBookImage(index))),
    })).filter((section) => section.books.length > 0)
    : [
      { key: 'featured', title: 'Truyện cổ tích', books: (homeData?.featured ?? []).map((item) => mapAudiobook(item, fairyTales[0].image)) },
      { key: 'latest', title: 'Thơ', books: (homeData?.latest ?? []).map((item) => mapAudiobook(item, poems[0].image)) },
      { key: 'popular', title: 'Truyện hay cho em', books: (homeData?.popular ?? []).map((item) => mapAudiobook(item, childrenStories[0].image)) },
    ].filter((section) => section.books.length > 0);

  const handleHomePress = () => {
    scrollRef.current?.scrollTo({ y: 0, animated: true });
    refreshHome(true, true);
  };

  return (
    <View style={styles.screen}>
      <ScrollView ref={scrollRef} contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.topHero}>
          <GradientBackground />
          <BrandHeader />
          {homeLoaded && narratorItems.length ? (
            <View style={styles.heroNarratorSection}>
              <SectionHeader title="Người đọc sách" actionText="Xem thêm" titleColor="#FFFFFF" onAction={() => navigation.navigate('PeopleList', { title: 'Người đọc sách' })} />
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.sliderContent}>
                {narratorItems.map((item) => (
                  <Pressable key={item.key} style={styles.authorItem} onPress={() => item.id && navigation.navigate('AuthorDetails', { id: item.id })}>
                    <Image source={item.image} style={styles.authorImage} resizeMode="cover" />
                    <Text style={styles.authorName} numberOfLines={2}>{item.name}</Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>
          ) : null}
        </View>

        {!homeLoaded ? (
          <HomeSkeleton />
        ) : (
          <>
            {continueTitle ? (
              <View style={styles.section}>
                <SectionHeader title="Xem tiếp" />
                <Pressable
                  style={styles.continueCard}
                  onPress={() => recent ? navigation.navigate('Player', { id: recent.id, slug: recent.slug }) : openBook(continueBook)}
                >
                  <Image source={continueImageSource} style={styles.continueImage} resizeMode="cover" />
                  <View style={styles.continueInfo}>
                    <Text style={styles.continueTitle}>{continueTitle}</Text>
                    <Text style={styles.continueAuthor}>{continueAuthor}</Text>
                    {continueChapter ? <Text style={styles.continueChapter}>{continueChapter}</Text> : null}
                    <View style={styles.progressTrack}>
                      <View style={[styles.progressFill, { width: `${continueProgress || 8}%` }]} />
                    </View>
                  </View>
                </Pressable>
              </View>
            ) : null}

            {homeSections.map((section) => (
              <View key={section.key} style={styles.section}>
                <SectionHeader title={section.title} actionText="Xem thêm" onAction={() => openDownloads(section.title, section.params)} />
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.sliderContent}>
                  {section.books.map((item) => (
                    <BookCard key={item.key} title={item.title} author={item.author} imageSource={item.image} onPress={() => openBook(item)} />
                  ))}
                </ScrollView>
              </View>
            ))}

            <View style={styles.section}>
              <SectionHeader title="Chủ đề" actionText="Xem thêm" onAction={() => openDownloads('Chủ đề')} />
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.sliderContent}>
                {categoryItems.map((item) => (
                  <Pressable
                    key={item.key}
                    style={styles.categoryCard}
                    onPress={() => openDownloads(item.label, item.type === 'topic' ? { topic_id: item.id } : { category_id: item.id })}
                  >
                    <Image source={item.image} style={styles.categoryImage} resizeMode="contain" />
                    <View style={styles.categoryOverlay} />
                    <Text style={styles.topicLabel}>{item.label}</Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>

          </>
        )}

        <View style={styles.sponsorSection}>
          <View style={styles.sponsorHeader}>
            <View>
              <Text style={styles.sponsorKicker}>Đơn vị đồng hành</Text>
              <Text style={styles.sponsorTitle}>Cùng bé lớn lên qua từng câu chuyện</Text>
            </View>
            <Ionicons name="open-outline" size={20} color={colors.button} />
          </View>
          <ScrollView
            ref={sponsorScrollRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.sponsorSlider}
            snapToInterval={sponsorCardWidth + sponsorCardGap}
            decelerationRate="fast"
          >
            {sponsors.map((sponsor) => (
              <Pressable
                key={sponsor.key}
                style={styles.sponsorLogoCard}
                disabled={!sponsor.url}
                onPress={() => sponsor.url && Linking.openURL(sponsor.url)}
              >
                <Image source={sponsor.logo} style={styles.sponsorLogo} resizeMode="contain" />
              </Pressable>
            ))}
          </ScrollView>
        </View>

        <View style={styles.journeyCard}>
          <Text style={styles.cardLabel}>Nghe ngẫu nhiên</Text>
          <Pressable style={styles.shuffleButton} onPress={() => navigation.navigate('GetSuggestions')}>
            <Ionicons name="shuffle" size={14} color={colors.buttonText} />
            <Text style={styles.shuffleText}>Chọn truyện cho em</Text>
          </Pressable>
        </View>
      </ScrollView>

      <BottomTabBar
        active="Home"
        onHome={handleHomePress}
        onDiscover={() => navigation.navigate('GetSuggestions')}
        onLibrary={() => navigation.navigate('Library')}
        onProfile={() => navigation.navigate('Settings')}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 26,
  },
  section: {
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  topHero: {
    overflow: 'hidden',
    borderBottomLeftRadius: 34,
    borderBottomRightRadius: 34,
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 16,
    marginBottom: 18,
  },
  heroNarratorSection: {
    marginTop: 6,
  },
  skeletonBlock: {
    backgroundColor: '#ECEEF2',
    overflow: 'hidden',
  },
  skeletonTitle: {
    width: 150,
    height: 20,
    borderRadius: 6,
    marginBottom: 16,
  },
  skeletonContinue: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  skeletonContinueImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
    marginRight: 24,
  },
  skeletonContinueText: {
    flex: 1,
    paddingTop: 4,
  },
  skeletonLineLarge: {
    width: '78%',
    height: 18,
    borderRadius: 6,
    marginBottom: 14,
  },
  skeletonLineSmall: {
    width: '46%',
    height: 12,
    borderRadius: 6,
    marginBottom: 16,
  },
  skeletonProgress: {
    width: '100%',
    height: 4,
    borderRadius: 100,
  },
  skeletonCard: {
    width: 140,
    marginRight: 16,
  },
  skeletonCover: {
    width: 140,
    height: 140,
    borderRadius: 8,
    marginBottom: 16,
  },
  skeletonBookTitle: {
    width: 118,
    height: 16,
    borderRadius: 6,
    marginBottom: 8,
    marginLeft: 4,
  },
  skeletonBookAuthor: {
    width: 82,
    height: 12,
    borderRadius: 6,
    marginLeft: 4,
  },
  continueCard: {
    width: '100%',
    minHeight: 104,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0E7BFF',
    borderRadius: 22,
    padding: 12,
    shadowColor: '#0069FF',
    shadowOpacity: 0.22,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    elevation: 6,
  },
  continueImage: {
    width: 76,
    height: 76,
    borderRadius: 18,
    backgroundColor: colors.card,
    marginRight: 14,
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  continueInfo: {
    flex: 1,
    paddingTop: 4,
  },
  continueTitle: {
    color: colors.buttonText,
    fontSize: 16,
    fontWeight: '900',
    lineHeight: 20,
    marginBottom: 12,
  },
  continueAuthor: {
    color: '#DDF3FF',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 16,
    marginBottom: 8,
  },
  continueChapter: {
    color: colors.yellow,
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16,
    marginBottom: 10,
  },
  progressTrack: {
    width: '100%',
    height: 7,
    borderRadius: 100,
    backgroundColor: 'rgba(255,255,255,0.35)',
    overflow: 'hidden',
  },
  progressFill: {
    height: 7,
    borderRadius: 100,
    backgroundColor: '#FFB200',
  },
  sliderContent: {
    paddingRight: 24,
  },
  categoryCard: {
    width: 132,
    height: 154,
    borderRadius: 24,
    overflow: 'hidden',
    marginRight: 16,
    backgroundColor: colors.panel,
    borderWidth: 3,
    borderColor: '#FFFFFF',
    shadowColor: '#39BFFF',
    shadowOpacity: 0.18,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 4,
  },
  categoryImage: {
    width: '100%',
    height: '100%',
  },
  categoryOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,105,255,0.18)',
  },
  categoryLabel: {
    position: 'absolute',
    left: 10,
    right: 10,
    bottom: 12,
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '900',
    textShadowColor: 'rgba(0,0,0,0.25)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  topicLabel: {
    position: 'absolute',
    left: 10,
    right: 10,
    bottom: 12,
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '900',
    textShadowColor: 'rgba(0,0,0,0.2)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  authorItem: {
    width: 96,
    alignItems: 'center',
    marginRight: 16,
  },
  authorImage: {
    width: 88,
    height: 88,
    borderRadius: 26,
    backgroundColor: colors.panel,
    marginBottom: 12,
    borderWidth: 3,
    borderColor: colors.yellow,
  },
  authorName: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '800',
    textAlign: 'center',
    textShadowColor: 'rgba(18, 51, 122, 0.22)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  journeyCard: {
    alignItems: 'center',
    alignSelf: 'stretch',
    marginHorizontal: 20,
    marginTop: 0,
    marginBottom: 28,
    backgroundColor: '#FFFFFF',
    borderRadius: 28,
    paddingVertical: 18,
  },
  cardLabel: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: '900',
    marginBottom: 16,
  },
  shuffleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.orange,
    borderRadius: 18,
    height: 48,
    minWidth: 210,
    justifyContent: 'center',
    paddingHorizontal: 16,
    gap: 8,
  },
  shuffleText: {
    color: colors.buttonText,
    fontWeight: '700',
    fontSize: 14,
  },
  sponsorSection: {
    backgroundColor: '#FF5CA8',
    borderRadius: 26,
    padding: 16,
    marginHorizontal: 20,
    marginBottom: 20,
    shadowColor: '#FF5CA8',
    shadowOpacity: 0.2,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
    elevation: 5,
  },
  sponsorHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 14,
  },
  sponsorKicker: {
    color: colors.yellow,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  sponsorTitle: {
    color: colors.buttonText,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 21,
  },
  sponsorSlider: {
    gap: sponsorCardGap,
    paddingRight: 4,
  },
  sponsorLogoCard: {
    width: sponsorCardWidth,
    height: 124,
    borderRadius: 0,
    backgroundColor: '#FFF9D9',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  sponsorLogo: {
    width: "100%",
    height: 120,
  },
  sponsorName: {
    color: colors.textSecondary,
    fontSize: 12,
    fontWeight: '600',
  },
});
